import { useQuery, useLazyQuery, useMutation } from '@apollo/react-hooks';
import { Button, Typography } from '@material-ui/core';
import History from '@jda/lui-common-icon-library/ui/History';
import React, { useEffect } from 'react';
import DetailedTable from '../../components/DetailedTable';
import AuditDetailsDialog from '../../components/AuditHistory';
import { DialogForm } from '../../components/DialogForm';
import {
  FETCH_AUTHENTICATION_QUERY,
  USER_DETAILS,
  VALIDATE_AUTHENTICATION_QUERY,
  DELETE_AUTHENTICATION_QUERY,
  FETCH_AUTHENTICATION_AUDIT_HISTORY_QUERY
} from '../graphQL/queries';
import columns from './columns';
import Edit from '@jda/lui-common-icon-library/ui/Edit';
import Trash from '@jda/lui-common-icon-library/ui/Trash';
import { Loading } from '../../Loading';
import AddAuthenticationDialog from './AddAuthenticationDialog';
import ViewDetails from '@jda/lui-common-icon-library/ui/ViewDetails';
import { useSnackbar } from 'notistack';

const validateAuths = [
  'OAUTH_CLIENT_CREDENTIALS_BODY',
  'OAUTH_CLIENT_CREDENTIALS_BASIC_AUTH',
  'OAUTH_CLIENT_CREDENTIALS_FORM'
];
interface AuthenticationParticipantProps {
  participant: any;
  workflowCode?: any;
}

const AuthenticationGrid = (props: AuthenticationParticipantProps) => {
  let { participantCode } = props.participant;
  const { enqueueSnackbar } = useSnackbar();
  const { loading, data, refetch } = useQuery(FETCH_AUTHENTICATION_QUERY, {
    variables: { participantCode: participantCode },
    fetchPolicy: 'network-only'
  });

  const { loading: auditLoading, data: auditData, refetch: auditRefetch } = useQuery(
    FETCH_AUTHENTICATION_AUDIT_HISTORY_QUERY,
    {
      variables: { participantCode: participantCode },
      fetchPolicy: 'network-only'
    }
  );

  const { data: userData } = useQuery(USER_DETAILS, { fetchPolicy: 'cache-only' });

  const [validateAuth, { data: authValidateData, error }] = useLazyQuery(VALIDATE_AUTHENTICATION_QUERY, {
    fetchPolicy: 'network-only'
  });

  const [deleteAuth] = useMutation(DELETE_AUTHENTICATION_QUERY);
  const [addAuthentication, setAddAuthentication] = React.useState(false);
  const [auditHistory, setAuditHistory] = React.useState(false);
  const [isActionOnSelect, showActionsOnToolbarSelect] = React.useState(false);
  const [formInfo, setFormInfo] = React.useState<any>({});
  const [formData, setFormData] = React.useState<any>({});
  const [edit, isEdit] = React.useState<any>(false);
  const [userRole, setUserRole] = React.useState('');
  const [isOpen, setIsopen] = React.useState<any>(false);
  const [authData, setAuthData] = React.useState<any>('');

  useEffect(() => {
    setUserRole(userData?.userProfile?.roles?.[0]);
  }, [userData]);

  useEffect(() => {
    if (authValidateData) {
      enqueueSnackbar('Authentication Validated successfully !!', {
        variant: 'success',
        preventDuplicate: true
      });
    } else if (error) {
      enqueueSnackbar('Authentication vaidate action failed !!', { variant: 'error', preventDuplicate: true });
    }
  }, [authValidateData, error]);

  const showValidate = (rowData: any) => {
    return validateAuths.includes(rowData.authMode);
  };

  const authenticationTableActions: any = [
    (userRole === 'APP_ADMIN' || userRole === 'PARTICIPANT_ADMIN') && {
      icon: () => (
        <Button
          variant="outlined"
          onClick={handleAddAuthenticationDialog}
          data-id="new-authentication"
          id="new-authentication"
          color="primary"
        >
          Add
        </Button>
      ),
      onClick: () => {},
      isFreeAction: !isActionOnSelect,
      position: isActionOnSelect ? 'toolbarOnSelect' : 'toolbar'
    },
    {
      icon: () => <History />,
      onClick: (event: any, rowData: any) => handleAuditHistory(rowData),
      tooltip: 'View History',
      position: 'row'
    },
    userRole === 'APP_SUPPORT' || userRole === 'PARTICIPANT_SUPPORT'
      ? {
          icon: () => <ViewDetails />,
          onClick: (event: any, rowData: any) => handleEditAuthentication(rowData),
          tooltip: 'View Details',
          position: 'row'
        }
      : {
          icon: () => <Edit data-id="edit-authentication" />,
          onClick: (event: any, rowData: any) => handleEditAuthentication(rowData)
        },
    (userRole === 'APP_ADMIN' || userRole === 'PARTICIPANT_ADMIN') &&
      ((rowData: any) => ({
        icon: () => !rowData?.defaultAuth && <Trash data-id="delete-authentication" />,
        onClick: (event: any, rowData: any) => handleDialogOpen(rowData)
      })),
    (rowData: any) => ({
      icon: () => (
        <Button
          variant="outlined"
          onClick={e => handleValidate(e, rowData)}
          data-id="new-authentication"
          id="new-authentication"
          color="primary"
        >
          Validate
        </Button>
      ),
      onClick: () => {},
      hidden: !showValidate(rowData)
    })
  ];

  const handleDialogOpen = (dataObj: any) => {
    setIsopen(true);
    setAuthData(dataObj);
  };

  const handleDialogClose = () => {
    setIsopen(false);
    setAuthData('');
  };

  const handleValidate = (e: any, dataObj: any) => {
    validateAuth({ variables: { participantCode: participantCode, authContext: dataObj.authContext } });
  };

  const handleAddAuthenticationDialog = () => {
    setFormInfo({});
    setFormData({ defaultAuth: 'no' });
    isEdit(false);
    setAddAuthentication(true);
  };

  const handleAddAuthenticationDialogClose = () => {
    setFormInfo({});
    setAddAuthentication(false);
  };

  const handleAuditHistoryClose = () => {
    setFormData({});
    setAuditHistory(false);
  };

  const handleAddAuthenticationDialogSubmit = (formData: any) => {
    setAddAuthentication(false);
  };

  const onSelectionAction = (data: Array<any>) => {
    if (data.length === 0) {
      showActionsOnToolbarSelect(false);
    } else {
      showActionsOnToolbarSelect(true);
    }
  };

  const handleEditAuthentication = (rowData: any) => {
    setFormData({ ...rowData });
    isEdit(true);
    setAddAuthentication(true);
  };

  const handleAuditHistory = (rowData: any) => {
    setFormData({ ...rowData });
    // isEdit(true);
    setAuditHistory(true);
  };

  const handleDeleteAuthentication = () => {
    deleteAuth({
      variables: {
        participantCode: participantCode,
        authContext: authData.authContext
      }
    })
      .then(response1 => {
        enqueueSnackbar('Authentication deleted successfully !!', { variant: 'success', preventDuplicate: true });
        refetch();
        handleDialogClose();
        return response1;
      })
      .catch(() => {
        enqueueSnackbar('Add Authentication action failed !!', { variant: 'error', preventDuplicate: true });
      });
  };
  return (
    <div>
      <AddAuthenticationDialog
        edit={edit}
        participant={props.participant}
        dialogOpen={addAuthentication}
        formInfo={formInfo}
        refresh={refetch}
        formData={formData}
        handleDialogClose={handleAddAuthenticationDialogClose}
        handleDialogSubmit={handleAddAuthenticationDialogSubmit}
        supportUser={userRole === 'APP_SUPPORT' || userRole === 'PARTICIPANT_SUPPORT' ? true : false}
      />
      <AuditDetailsDialog
        dialogOpen={auditHistory}
        handleDialogClose={handleAuditHistoryClose}
        formData={auditData?.fetchAuditAuthentication?.content || []}
        mode={'code'}
        participantCode={participantCode}
        refresh={auditLoading}
        totalElements={auditData?.fetchAuditAuthentication?.totalElements}
      />
      {loading ? (
        <Loading />
      ) : (
        <DetailedTable
          key="AuthenticationInfo"
          id="authenticationGrid"
          title=""
          columns={columns}
          data={data?.fetchAuthentication?.content || []}
          showSelection={false}
          showPagination={true}
          page={0}
          rowsPerPage={20}
          tableActions={authenticationTableActions}
          onSelection={onSelectionAction}
          labelRowsPerPage="Authenticate Per Page"
        />
      )}
      <DialogForm
        openFn={isOpen}
        dataConfig={[]}
        handleDialogClose={handleDialogClose}
        handleDialogSubmit={handleDeleteAuthentication}
        submitlabel={'Delete'}
        title={`Delete Authentication`}
        dialogId="delete-authentication-id"
        closeFn={handleDialogClose}
        maxWidth="sm"
      >
        <Typography color="textPrimary">{`Are you sure want to delete ${authData.authContext} ?`}</Typography>
      </DialogForm>
    </div>
  );
};
export default AuthenticationGrid;
